import type { ElevenLabsTranscriptionConfig } from '../transcription/ElevenLabsTranscriptionConfig';

import {
   StyledDebugGrid,
   StyledDebugInput,
   StyledDebugLabel,
   StyledDebugRoot,
   StyledDebugRow,
   StyledDebugSectionTitle,
   StyledDebugSelect,
   StyledDebugSummary,
} from './TranscriptionDebugPanel.styled';

type ElevenLabsDebugPanelProps = {
   config: ElevenLabsTranscriptionConfig;
   onChange: (config: ElevenLabsTranscriptionConfig) => void;
   disabled: boolean;
};

/**
 * Configuration panel for ElevenLabs Scribe provider.
 * Shows audio constraints and VAD tuning parameters for experimenting
 * with segment granularity (pseudo-turn detection in multi-speaker scenarios).
 */
// eslint-disable-next-line max-lines-per-function
export const ElevenLabsDebugPanel = (props: ElevenLabsDebugPanelProps): JSX.Element => {
   const { config, onChange, disabled } = props;

   const set = <K extends keyof ElevenLabsTranscriptionConfig>(
      key: K,
      value: ElevenLabsTranscriptionConfig[K]
   ): void => {
      onChange({ ...config, [key]: value });
   };

   return (
      <StyledDebugRoot $disabled={disabled}>
         <StyledDebugSummary>{'Konfiguracja mikrofonu (ElevenLabs)'}</StyledDebugSummary>
         <StyledDebugGrid>
            <StyledDebugSectionTitle>{'Audio'}</StyledDebugSectionTitle>

            <StyledDebugRow>
               <StyledDebugLabel>{'Eliminacja echa'}</StyledDebugLabel>
               <StyledDebugSelect
                  id={'echoCancel'}
                  value={config.echoCancellation ? 'on' : 'off'}
                  onChange={(event) => set('echoCancellation', event.target.value === 'on')}
               >
                  <option value={'on'}>{'Wł'}</option>
                  <option value={'off'}>{'Wył'}</option>
               </StyledDebugSelect>
            </StyledDebugRow>

            <StyledDebugRow>
               <StyledDebugLabel>{'Tłumienie szumu'}</StyledDebugLabel>
               <StyledDebugSelect
                  id={'noiseSup'}
                  value={config.noiseSuppression ? 'on' : 'off'}
                  onChange={(event) => set('noiseSuppression', event.target.value === 'on')}
               >
                  <option value={'on'}>{'Wł'}</option>
                  <option value={'off'}>{'Wył'}</option>
               </StyledDebugSelect>
            </StyledDebugRow>

            <StyledDebugRow>
               <StyledDebugLabel>{'Auto gain'}</StyledDebugLabel>
               <StyledDebugSelect
                  id={'autoGain'}
                  value={config.autoGainControl ? 'on' : 'off'}
                  onChange={(event) => set('autoGainControl', event.target.value === 'on')}
               >
                  <option value={'on'}>{'Wł'}</option>
                  <option value={'off'}>{'Wył'}</option>
               </StyledDebugSelect>
            </StyledDebugRow>

            <StyledDebugRow>
               <StyledDebugLabel>{'Filtr tła'}</StyledDebugLabel>
               <StyledDebugSelect
                  id={'filterBg'}
                  value={config.filterBackgroundAudio ? 'on' : 'off'}
                  onChange={(event) => set('filterBackgroundAudio', event.target.value === 'on')}
               >
                  <option value={'on'}>{'Wł'}</option>
                  <option value={'off'}>{'Wył'}</option>
               </StyledDebugSelect>
            </StyledDebugRow>

            <StyledDebugRow>
               <StyledDebugLabel>{'Niskie opóźnienie'}</StyledDebugLabel>
               <StyledDebugSelect
                  id={'lowLatency'}
                  value={config.lowLatency ? 'on' : 'off'}
                  onChange={(event) => set('lowLatency', event.target.value === 'on')}
               >
                  <option value={'on'}>{'Wł'}</option>
                  <option value={'off'}>{'Wył'}</option>
               </StyledDebugSelect>
            </StyledDebugRow>

            <StyledDebugSectionTitle>{'VAD (segmentacja)'}</StyledDebugSectionTitle>

            <StyledDebugRow>
               <StyledDebugLabel>{'Próg ciszy (s)'}</StyledDebugLabel>
               <StyledDebugInput
                  id={'vadSilenceThreshold'}
                  type={'number'}
                  min={0.3}
                  max={3.0}
                  step={0.1}
                  value={config.vadSilenceThresholdSecs}
                  onChange={(event) => set('vadSilenceThresholdSecs', Number(event.target.value))}
               />
            </StyledDebugRow>

            <StyledDebugRow>
               <StyledDebugLabel>{'Czułość VAD'}</StyledDebugLabel>
               <StyledDebugInput
                  id={'vadThreshold'}
                  type={'number'}
                  min={0.1}
                  max={0.9}
                  step={0.05}
                  value={config.vadThreshold}
                  onChange={(event) => set('vadThreshold', Number(event.target.value))}
               />
            </StyledDebugRow>

            <StyledDebugRow>
               <StyledDebugLabel>{'Min mowa (ms)'}</StyledDebugLabel>
               <StyledDebugInput
                  id={'minSpeechDuration'}
                  type={'number'}
                  min={50}
                  max={2000}
                  step={50}
                  value={config.minSpeechDurationMs}
                  onChange={(event) => set('minSpeechDurationMs', Number(event.target.value))}
               />
            </StyledDebugRow>

            <StyledDebugRow>
               <StyledDebugLabel>{'Min cisza (ms)'}</StyledDebugLabel>
               <StyledDebugInput
                  id={'minSilenceDuration'}
                  type={'number'}
                  min={50}
                  max={2000}
                  step={50}
                  value={config.minSilenceDurationMs}
                  onChange={(event) => set('minSilenceDurationMs', Number(event.target.value))}
               />
            </StyledDebugRow>
         </StyledDebugGrid>
      </StyledDebugRoot>
   );
};
